import { IRestaurant, List, ListItem, Pill, TPillTone } from '@localina/core';
import { RestaurantLogoIcon } from '@localina/icons';
import { Feedback, Hub, Language, MenuBook } from '@mui/icons-material';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useRestaurant } from '../../api/queries/restaurants';
import { useRestaurantMenus } from '../../api/queries/restaurantMenu';
import { Page, PlatformsIntegrationView, RestaurantLogoIntegrationView, WidgetIntegrationView } from '../../components';
import { Path } from '../../enums';
import { IPathParams, PathUtils } from '../../utils';
import { useHaveAccountFeatures } from '../../api/queries/account';
import { compact } from 'lodash';
import { FeedbackIntegrationView } from '../../components/IntegrationViews/FeedbackIntegration/FeedbackIntegrationView';

export enum IntegrationItem {
    WIDGET = 'widget',
    LOGO = 'logo',
    PLATFORMS = 'platforms',

    MENU = 'menu',
    FEEDBACK = 'feedback',
}

type TIntegrationMenuPathParams = Pick<IPathParams, 'integrationId' | 'restaurantId'>;

const Integrations: React.FC = () => {
    const { t } = useTranslation();
    const params = useParams() as unknown as TIntegrationMenuPathParams;
    const navigate = useNavigate();

    const restaurantQuery = useRestaurant();

    const handleItemClick = (item: IntegrationItem) => () => {
        navigate(
            PathUtils.generateUrl(Path.RESTAURANT_SETTINGS_INTEGRATION, {
                restaurantId: params.restaurantId,
                integrationId: item,
            }),
        );
    };

    const handleCloseDrawer = useCallback(() => {
        navigate(
            PathUtils.generateUrl(Path.RESTAURANT_SETTINGS_INTEGRATIONS, {
                restaurantId: params.restaurantId,
            }),
        );
    }, [params.restaurantId]);

    const getIconForIntegrationItem = (item: IntegrationItem) => {
        switch (item) {
            case IntegrationItem.WIDGET:
                return <Language />;
            case IntegrationItem.LOGO:
                return <RestaurantLogoIcon />;
            case IntegrationItem.PLATFORMS:
                return <Hub />;
            case IntegrationItem.MENU:
                return <MenuBook />;
            case IntegrationItem.FEEDBACK:
                return <Feedback />;
        }
    };

    useEffect(() => {
        if (params.integrationId && !Object.values(IntegrationItem).includes(params.integrationId)) {
            handleCloseDrawer();
        }
    }, [params.integrationId]);

    const [canUseMenu, canUseLogo, canUseFeedback] = useHaveAccountFeatures(['menu', 'restaurantLogo', 'feedback']);

    const menusQuery = useRestaurantMenus({ enabled: canUseMenu });
    const menuEntries = menusQuery.data?.pages.flatMap((page) => page.items) ?? [];

    const menuEntriesTruncated = Boolean(menusQuery.data?.pages[menusQuery.data.pages.length - 1]?.hasNext);

    const getStatus = (item: IntegrationItem, restaurant: IRestaurant): { tone: TPillTone; label: string } | null => {
        const config = restaurant.configuration;
        switch (item) {
            case IntegrationItem.WIDGET:
                return null;
            case IntegrationItem.LOGO:
                return config.logo
                    ? { tone: 'good', label: t('integrations.status.uploaded') }
                    : { tone: 'neutral', label: t('integrations.status.notUploaded') };
            case IntegrationItem.PLATFORMS:
                return config.platforms?.length
                    ? { tone: 'good', label: t('integrations.status.connected', { count: config.platforms.length }) }
                    : { tone: 'neutral', label: t('integrations.status.notConnected') };
            case IntegrationItem.MENU:
                if (!menuEntries.length) {
                    return { tone: 'warn', label: t('integrations.status.noMenuEntries') };
                }
                return {
                    tone: 'good',
                    label: t(
                        menuEntriesTruncated
                            ? 'integrations.status.menuEntriesMore'
                            : 'integrations.status.menuEntries',
                        { count: menuEntries.length },
                    ),
                };
            case IntegrationItem.FEEDBACK:
                return config.feedbackActivated
                    ? { tone: 'good', label: t('integrations.status.active') }
                    : { tone: 'neutral', label: t('integrations.status.notConfigured') };
            default:
                return null;
        }
    };

    return (
        <Page
            name="integrations"
            title={{ value: t('integrations.title'), subtitle: t('integrations.subtitle') }}
            isLoading={restaurantQuery.isInitialLoading}
        >
            <List>
                {compact([
                    IntegrationItem.WIDGET,
                    canUseLogo && IntegrationItem.LOGO,
                    IntegrationItem.PLATFORMS,
                    canUseMenu && IntegrationItem.MENU,
                    canUseFeedback && IntegrationItem.FEEDBACK,
                ]).map((item) => {
                    const status = restaurantQuery.data ? getStatus(item, restaurantQuery.data) : null;
                    return (
                        <ListItem
                            key={item}
                            classNames="integration-listitem"
                            leftIcon={
                                <span className="integration-listitem__icon">{getIconForIntegrationItem(item)}</span>
                            }
                            title={
                                <div className="integration-listitem__row">
                                    <div className="integration-listitem__main">
                                        <span className="integration-listitem__name">
                                            {t(`integrations.items.${item}.title`)}
                                        </span>
                                        <span className="integration-listitem__subtitle">
                                            {t(`integrations.items.${item}.subtitle`)}
                                        </span>
                                    </div>
                                    {status && <Pill tone={status.tone} label={status.label} />}
                                </div>
                            }
                            onClick={handleItemClick(item)}
                        />
                    );
                })}
            </List>
            {restaurantQuery.data && (
                <>
                    <WidgetIntegrationView
                        isOpen={params.integrationId === IntegrationItem.WIDGET}
                        onClose={handleCloseDrawer}
                        restaurant={restaurantQuery.data}
                    />
                    <RestaurantLogoIntegrationView
                        isOpen={params.integrationId === IntegrationItem.LOGO && canUseLogo}
                        onClose={handleCloseDrawer}
                        restaurant={restaurantQuery.data}
                    />
                    <PlatformsIntegrationView
                        isOpen={params.integrationId === IntegrationItem.PLATFORMS}
                        onClose={handleCloseDrawer}
                        restaurant={restaurantQuery.data}
                    />
                    <FeedbackIntegrationView
                        isOpen={params.integrationId === IntegrationItem.FEEDBACK && canUseFeedback}
                        onClose={handleCloseDrawer}
                        restaurant={restaurantQuery.data}
                    />
                </>
            )}
        </Page>
    );
};

export default Integrations;
